import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Button
} from 'reactstrap';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import * as Icon from 'react-feather';
import html2canvas from 'html2canvas';
import { useDispatch } from 'react-redux';

import { callLogDetailsGetById } from '../../../store/slices/tenantSlices/dailyReportSlice';
import { formatDuration } from '../../../utils/common';
import CallexLoader from '../../../components/CallexLoader';

const CallDetailsModal = ({ isOpen, onClose, call_log_id }) => {
  const dispatch = useDispatch();

  const chatRef = useRef(null);

  const [mode, setMode] = useState('simple');
  const [activeTab, setActiveTab] = useState('chat');
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCallLogDetailsGet = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(callLogDetailsGetById(call_log_id)).unwrap();
      if (response && response.success) {
        setDetails(response?.data?.[0] || null);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (call_log_id) {
      fetchCallLogDetailsGet();
    }
  }, [call_log_id]);

  /* ================= FORMATTERS ================= */

  const formattedCallTime = useMemo(() => {
    if (!details?.time_of_call) return '-';
    const date = new Date(details.time_of_call);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [details]);

  const sentimentColor =
    details?.sentiment_score === 'Positive'
      ? 'success'
      : details?.sentiment_score === 'Negative'
        ? 'danger'
        : 'warning';

  const callStatusColors = {
    completed: '#2cd07e33', // Green
    busy: '#ffb22b33', // Yellow
    failed: '#ff456033', // Red
    'no-answer': '#545b6233' // Gray
  };

  /* ================= RENDER CHAT ================= */

  const renderChat = () => {
    if (!details?.interactions?.length) return null;

    const messages = [];

    const sorted = [...details.interactions]
      .filter((i) => i.user_query || i.bot_response)
      .sort((a, b) => a.interaction_sequence - b.interaction_sequence);

    sorted.forEach((interaction) => {
      if (interaction.user_query) {
        messages.push({
          type: 'user',
          text: interaction.user_query,
          time: interaction.time_of_call
        });
      }
      if (interaction.bot_response) {
        messages.push({
          type: 'assistant',
          text: interaction.bot_response,
          time: interaction.time_of_call
        });
      }
    });

    return (
      <div className="chat-wrapper" ref={chatRef}>
        {messages.map((msg, index) => {
          return (
            <div key={index} className={`chat-row ${msg.type}`}>
              {msg.type === 'assistant' && (
                <div className="avatar assistant">
                  <Icon.Headphones size={16} />
                </div>
              )}

              <div className="message-group">
                <div className="chat-bubble">{msg.text}</div>
                <div className="chat-time">
                  {new Date(msg.time).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              {msg.type === 'user' && (
                <div className="avatar user">
                  <Icon.User size={16} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /* ================= RENDER ANALYSIS ================= */

  const renderAnalysis = () => (
    <div className="analysis-section">
      <Row>
        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Extracted Variables</div>

            <div className="variables-grid">
              {Object.entries(details?.extracted_variables || {}).map(([key, value]) => (
                <div key={key} className="variable-item">
                  <div className="variable-label">{key.replaceAll('_', ' ')}</div>
                  <div className="variable-value">{value || 'Not provided'}</div>
                </div>
              ))}
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Call Analysis</div>

            <div className="sentiment-row">
              <span>Sentiment Score</span>
              <span className={`sentiment-pill ${sentimentColor}`}>{details?.sentiment_score}</span>
            </div>

            <div className="analysis-box">
              <div className="analysis-subtitle">Analysis Details</div>
              <div className="analysis-description">{details?.sentiment_analysis_details}</div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  /* ================= RENDER EVALUATIONS ================= */
  const renderEvaluations = () => (
    <div className="analysis-section">
      <Row>
        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Metrics</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Intent Score</div>
                <div className="variable-value">{details?.metric_score_intent ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Relevance Score</div>
                <div className="variable-value">{details?.metric_score_relevance ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Latency Score</div>
                <div className="variable-value">{details?.metric_score_latency ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Coherence Score</div>
                <div className="variable-value">{details?.metric_score_coherence ?? 0}</div>
              </div>
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Metrics</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Sentiment</div>
                <div className="variable-value">{details?.sentiment_score || 'N/A'}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">CQS Score</div>
                <div className="variable-value">{details?.cqs_score ?? 0}</div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  /* ================= RENDER LLM ================= */
  const renderLlmDetails = () => (
    <div className="analysis-section">
      <Row>
        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Model Details</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Model</div>
                <div className="variable-value">{details?.model_name ?? 'N/A'}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Vendor</div>
                <div className="variable-value">{'N/A'}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Type</div>
                <div className="variable-value">{details?.model_type ?? 'N/A'}</div>
              </div>
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Usage & Cost</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Total Tokens</div>
                <div className="variable-value">{details?.total_tokens ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Cost</div>
                <div className="variable-value">
                  ${details?.aggregated_estimated_cost?.toFixed(3) ?? '0.000'}
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col md="12">
          <div
            className="analysis-card"
            style={{ marginTop: '1.5rem', marginBottom: '1.5rem', height: 'max-content' }}>
            <div className="analysis-header">Usage & Cost</div>
            <div className="variable-label" style={{ textTransform: 'none', fontSize: '12px' }}>
              Duration: {formatDuration(details?.call_duration || '0:00')}
            </div>
            <div className="variable-value">{details?.call_conversation}</div>
          </div>
        </Col>
      </Row>
    </div>
  );
  /* ================= RENDER LATENCY ================= */

  const renderLatency = () => {
    const interactions = details?.interactions || [];

    // ---------- Helpers ----------
    const getPercentile = (arr, percentile) => {
      if (!arr.length) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const index = Math.floor((percentile / 100) * (sorted.length - 1));
      return sorted[index] || 0;
    };

    const getStdDev = (arr) => {
      if (!arr.length) return 0;
      const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
      const variance = arr.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / arr.length;
      return Math.sqrt(variance);
    };

    // ---------- Extract Values ----------
    const llmLatencies = interactions.map((i) => i.latency_llm || 0);
    const ttsLatencies = interactions.map((i) => i.tts_time || 0);
    const ttsDurations = interactions.map((i) => i.tts_speaking_duration || 0);

    // ---------- Totals ----------
    const totalLLM = llmLatencies.reduce((a, b) => a + b, 0);
    const totalTTS = ttsLatencies.reduce((a, b) => a + b, 0);
    const totalTTSDuration = ttsDurations.reduce((a, b) => a + b, 0);

    // ---------- Averages ----------
    const avgLLM = llmLatencies.length > 0 ? totalLLM / llmLatencies.length : 0;

    const avgTTS = ttsLatencies.length > 0 ? totalTTS / ttsLatencies.length : 0;

    const avgTTSDuration = ttsDurations.length > 0 ? totalTTSDuration / ttsDurations.length : 0;

    // Total = LLM + TTS latency (NOT total_response_time)
    const avgTotal =
      interactions.length > 0
        ? interactions.reduce((acc, row) => acc + (row.latency_llm || 0) + (row.tts_time || 0), 0) /
          interactions.length
        : 0;

    // ---------- Statistics ----------
    const llmP50 = getPercentile(llmLatencies, 50);
    const llmP95 = getPercentile(llmLatencies, 95);
    const llmStd = getStdDev(llmLatencies);

    const ttsP50 = getPercentile(ttsLatencies, 50);
    const ttsP95 = getPercentile(ttsLatencies, 95);
    const ttsStd = getStdDev(ttsLatencies);

    return (
      <div className="latency-section">
        {/* ===== Top Row ===== */}
        <Row className="g-4">
          <Col md="6" className="pe-2">
            <div className="latency-card">
              <h6 className="section-title">Active Services</h6>
              <div className="variables-grid">
                <div className="variable-item">
                  <span className="variable-label">LLM Model</span>
                  <span className="variable-value">{details?.model_name || '-'}</span>
                </div>

                <div className="variable-item">
                  <span className="variable-label">Channel Type</span>
                  <span className="variable-value">{details?.channel_type || '-'}</span>
                </div>

                <div className="variable-item">
                  <span className="variable-label">TTS Service</span>
                  <span className="variable-value">{details?.tts_service || '-'}</span>
                </div>

                <div className="variable-item">
                  <span className="variable-label">ASR/STT Service</span>
                  <span className="variable-value">
                    {details?.asr_service || details?.stt_service || '-'}
                  </span>
                </div>
              </div>
            </div>
          </Col>

          <Col md="6" className="ps-2">
            <div className="latency-card">
              <h6 className="section-title">Latency Profile</h6>

              <div className="latency-grid">
                <div className="metric-box">
                  <h4>{totalLLM.toFixed(2)}s</h4>
                  <span>Total LLM Latency</span>
                </div>

                <div className="metric-box success">
                  <h4>{totalTTS.toFixed(2)}s</h4>
                  <span>Total TTS Latency</span>
                </div>

                <div className="metric-box">
                  <h4>{avgLLM.toFixed(2)}s</h4>
                  <span>Average LLM Latency</span>
                </div>

                <div className="metric-box success">
                  <h4>{totalTTSDuration.toFixed(2)}s</h4>
                  <span>Total TTS Duration</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ===== Stats Row ===== */}
        <Row className="g-4 mt-2">
          <Col md="6" className="mt-2 pe-2">
            <div className="latency-card">
              <h6 className="section-title">LLM Latency Statistics</h6>
              <div className="stat-row">
                <div className="mini-box">
                  <strong>{llmP50.toFixed(2)}s</strong>
                  <span>P50</span>
                </div>
                <div className="mini-box">
                  <strong>{llmP95.toFixed(2)}s</strong>
                  <span>P95</span>
                </div>
                <div className="mini-box">
                  <strong>{llmStd.toFixed(2)}s</strong>
                  <span>Std Dev</span>
                </div>
              </div>
            </div>
          </Col>

          <Col md="6" className="mt-2 ps-2">
            <div className="latency-card">
              <h6 className="section-title">TTS Latency Statistics</h6>
              <div className="stat-row">
                <div className="mini-box">
                  <strong>{ttsP50.toFixed(2)}s</strong>
                  <span>P50</span>
                </div>
                <div className="mini-box">
                  <strong>{ttsP95.toFixed(2)}s</strong>
                  <span>P95</span>
                </div>
                <div className="mini-box">
                  <strong>{ttsStd.toFixed(2)}s</strong>
                  <span>Std Dev</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ===== Detailed Breakdown ===== */}
        <Row className="mt-3">
          <Col>
            <div className="latency-card">
              <h6 className="section-title">Detailed Breakdown</h6>

              <div className="table-responsive">
                <table className="latency-table">
                  <thead>
                    <tr>
                      <th>Interaction</th>
                      <th>LLM (s)</th>
                      <th>TTS Latency (s)</th>
                      <th>TTS Duration (s)</th>
                      <th>Total (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interactions.map((row) => (
                      <tr key={row.id}>
                        <td>#{row.interaction_sequence}</td>

                        <td>
                          {row.latency_llm?.toFixed(2) || '0.00'}
                          {row.total_tokens ? ` (${row.total_tokens} tokens)` : ''}
                        </td>

                        <td>{row.tts_time?.toFixed(2) || '0.00'}</td>

                        <td>{row.tts_speaking_duration?.toFixed(2) || '0.00'}</td>

                        <td>{((row.latency_llm || 0) + (row.tts_time || 0)).toFixed(2)}</td>
                      </tr>
                    ))}

                    {/* ===== Average Row ===== */}
                    <tr className="average-row">
                      <td>
                        <strong>Average</strong>
                      </td>
                      <td>{avgLLM.toFixed(2)}</td>
                      <td>{avgTTS.toFixed(2)}</td>
                      <td>{avgTTSDuration.toFixed(2)}</td>
                      <td>{avgTotal.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const interactionTab = () => {
    if (!details?.interactions?.length) {
      return <div className="analysis-card">No interactions found.</div>;
    }

    return (
      <div className="interaction-wrapper">
        {details?.interactions.map((item, index) => (
          <div className="interaction-card" key={index}>
            <div className="interaction-header">
              <div>
                <div className="interaction-title">Interaction #{index + 1}</div>
                <div className="interaction-meta">{item.time_of_call}</div>
              </div>

              <div className="interaction-meta">
                <div>Total Response Time: {item.total_response_time?.toFixed(2) || '0.00'}s</div>
                <div>Total Tokens: {item.total_tokens || 0}</div>
              </div>
            </div>

            {item.user_query && (
              <div className="message-box">
                <div className="message-label">Caller</div>
                {item.user_query}
              </div>
            )}

            {item.bot_response && (
              <div className="message-box message-assistant">
                <div className="message-label">Assistant</div>
                {item.bot_response}
              </div>
            )}

            <div className="score-grid">
              <div className="score-card">
                <div className="score-label">Intent Score</div>
                <div className="score-value">{item?.metric_score_intent ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">Relevance Score</div>
                <div className="score-value">{item?.metric_score_relevance ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">Coherence Score</div>
                <div className="score-value">{item?.metric_score_coherence ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">Latency Score</div>
                <div className="score-value">{item?.metric_score_latency ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">LLM Latency Score</div>
                <div className="score-value">{(item?.latency_llm ?? 0).toFixed(2)}s</div>
              </div>

              <div className="score-card">
                <div className="score-label">ASR Latency Score</div>
                <div className="score-value">{(item?.asr_time ?? 0).toFixed(2)}s</div>
              </div>

              <div className="score-card">
                <div className="score-label">TTS Latency Score</div>
                <div className="score-value">{(item?.tts_time ?? 0).toFixed(2)}s</div>
              </div>
            </div>

            <div className="evaluation-section">
              <div className="evaluation-title">Evaluation</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleDownloadChatPDF = async () => {
    const canvas = await html2canvas(chatRef.current, { scale: 2 });

    const imgData = canvas.toDataURL('image/png');

    // eslint-disable-next-line new-cap
    const pdf = new jsPDF('p', 'mm', 'a4');

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save('chat-transcript.pdf');
  };

  const handleDownloadCSV = () => {
    if (!details) return;

    const formatLabel = (text) =>
      text
        ?.split('_')
        ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        ?.join(' ');

    const extractedVars = details?.extracted_variables || {};

    const formattedVariables = Object.entries(extractedVars).reduce((acc, [key, value]) => {
      acc[formatLabel(key)] = value || '';
      return acc;
    }, {});

    const baseData = {
      'Call Date': details?.time_of_call || '',
      Name: details?.bot_name || '',
      'From Number': details?.from_number || '',
      'To Number': details?.to_number || '',
      Duration: details?.call_duration || '',
      Status: details?.call_status || ''
    };

    const finalData = {
      ...baseData,
      ...formattedVariables,
      'Sentiment Score': details?.sentiment_score || '',
      'Analysis Details': details?.sentiment_analysis_details || ''
    };

    const headers = Object.keys(finalData);
    const values = Object.values(finalData);

    const csvContent = headers.join(',') + '\n' + values.map((v) => `"${v ?? ''}"`).join(',');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = `call-details-${details?.id || 'record'}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const statusColor = callStatusColors[details?.call_status?.toLowerCase()] || '#545b62';

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered>
      <ModalHeader toggle={onClose}>Call Details</ModalHeader>
      {isLoading ? (
        <ModalBody className="call-details-modal">
          <div
            className="p-0 d-flex justify-content-center align-items-center m-0"
            style={{ height: 'calc(100vh - 200px)' }}>
            <CallexLoader />
          </div>
        </ModalBody>
      ) : (
        <ModalBody className="call-details-modal">
          {/* ================= TOP MODE SWITCH ================= */}
          <div className="d-flex justify-content-between align-items-baseline">
            <div className="mode-switch">
              <div className={`mode-indicator ${mode === 'simple' ? 'left' : 'right'}`} />

              <div
                className={`mode-card ${mode === 'simple' ? 'active' : ''}`}
                onClick={() => {
                  setMode('simple');
                  setActiveTab('chat');
                }}>
                Simple
              </div>

              <div
                className={`mode-card ${mode === 'advanced' ? 'active' : ''}`}
                onClick={() => {
                  setMode('advanced');
                  setActiveTab('interaction');
                }}>
                Advanced
              </div>
            </div>
            <Button
              id="downloadChatBtn"
              color="primary"
              size="sm"
              className="d-flex align-items-center py-2 px-2"
              onClick={handleDownloadCSV}>
              <Icon.Download size={16} />
            </Button>
          </div>
          {/* ================= COMMON INFO ================= */}
          <div className="common-info">
            <Row>
              <Col md="4">
                <div className="info-card">
                  <div className="info-label">Source</div>
                  <div className="info-value">{details?.bot_name || '-'}</div>
                </div>
              </Col>

              <Col md="4">
                <div className="info-card">
                  <div className="info-label">Call Time</div>
                  <div className="info-value">{formattedCallTime}</div>
                </div>
              </Col>

              <Col md="4">
                <div className="info-card">
                  <div className="info-label">Call Info</div>
                  <div className="info-value">
                    <span className="duration">{details?.channel_type}</span>
                    <span className="duration">{details?.call_duration}</span>
                    <span className="status-badge" style={{ backgroundColor: statusColor }}>
                      {details?.call_status}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
          {/* ================= SIMPLE TABS ================= */}
          {mode === 'simple' ? (
            <>
              <div className="d-flex justify-content-between align-items-center">
                <Nav tabs className="inner-tabs call-details-tab">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === 'chat' })}
                      onClick={() => setActiveTab('chat')}>
                      Chat
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === 'analysis' })}
                      onClick={() => setActiveTab('analysis')}>
                      Analysis
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === 'latency' })}
                      onClick={() => setActiveTab('latency')}>
                      Latency Profile
                    </NavLink>
                  </NavItem>
                </Nav>

                {activeTab === 'chat' && (
                  <Button
                    id="downloadChatBtn"
                    color="primary"
                    size="sm"
                    className="d-flex align-items-center py-2 px-2"
                    onClick={handleDownloadChatPDF}>
                    <Icon.Download size={16} className="me-1" />
                    Export Chat
                  </Button>
                )}
              </div>

              <TabContent activeTab={activeTab} className="pt-3">
                <TabPane tabId="chat">{renderChat()}</TabPane>
                <TabPane tabId="analysis">{renderAnalysis()}</TabPane>
                <TabPane tabId="latency">{renderLatency()}</TabPane>
              </TabContent>
            </>
          ) : (
            <>
              <Nav tabs className="inner-tabs call-details-tab">
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'interaction' })}
                    onClick={() => setActiveTab('interaction')}>
                    Interaction
                  </NavLink>
                </NavItem>

                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'evaluations' })}
                    onClick={() => setActiveTab('evaluations')}>
                    Evaluations
                  </NavLink>
                </NavItem>

                {/* <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'llm' })}
                    onClick={() => setActiveTab('llm')}>
                    LLM Details & Cost
                  </NavLink>
                </NavItem> */}

                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'latency' })}
                    onClick={() => setActiveTab('latency')}>
                    Latency Profile
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab} className="pt-3">
                <TabPane tabId="interaction">{interactionTab()}</TabPane>
                <TabPane tabId="evaluations">{renderEvaluations()}</TabPane>
                <TabPane tabId="llm">{renderLlmDetails()}</TabPane>
                <TabPane tabId="latency">{renderLatency()}</TabPane>
              </TabContent>
            </>
          )}
        </ModalBody>
      )}
    </Modal>
  );
};

CallDetailsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  call_log_id: PropTypes.string
};

export default CallDetailsModal;
